import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { NavLink, useLocation, useNavigate } from 'react-router-dom'
import {
  ChevronRight,
  LayoutDashboard,
  DollarSign,
  Megaphone,
  Camera,
  Receipt,
  Bell,
  Mail,
  Package2,
  Grid3x3,
  FileSpreadsheet,
  Store,
  Briefcase,
  Settings,
  Printer,
  BadgePercent,
  Workflow,
  Rocket,
  Users,
  Sparkles,
  Calendar,
  CalendarRange,
  ListChecks,
  FolderOpen,
  Ticket,
  TrendingUp,
  CheckSquare,
  Inbox as InboxIcon,
  FileText,
  Scale,
  FlaskConical,
  Building,
  Building2,
  ShieldCheck,
  Truck,
  BookCheck,
  Boxes,
  Fuel,
  Car,
  ClipboardSignature,
  Wallet,
  QrCode,
  Barcode,
  X,
  PackageCheck,
  BookOpen,
  Lock,
  Search,
  Star,
  PanelLeftClose,
  PanelLeftOpen,
  Handshake,
  Package,
  Landmark,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ROLURI_DECONTURI_ECHIPA, ROLURI_FLOTA, type Permission } from '@b4l/shared'
import { useQuery } from '@tanstack/react-query'
import { useAuthStore } from '../stores/auth'
import { api } from '../lib/api'
import { setariVizibile } from '../pages/settings/SetariPage'

export interface NavItem {
  to: string
  label: string
  icon: LucideIcon
  permission?: Permission
  /** Doar pentru aceste roluri, peste permisiune (ex. deconturile tuturor — vezi
   *  `ROLURI_DECONTURI_ECHIPA`). Serverul refuza oricum; aici doar nu se arata linkul. */
  roluri?: readonly string[]
  /**
   * Al doilea nivel de grupare, in interiorul grupului. Titlu, nu nod pliabil: un al doilea
   * nivel de pliere ar ascunde intrari in doua clicuri.
   *
   * Sta AICI, nu pe `Page` din catalogul de pagini: bara laterala nu se genereaza din catalog,
   * are lista proprie.
   */
  section?: string
}

export interface NavGroup {
  label: string
  icon: LucideIcon
  items: NavItem[]
  /**
   * Manualul grupului, deschis intr-un tab nou. Fisierele stau in `apps/web/public/manuale/`,
   * deci ajung pe `crm.boost4life.ro/manuale/...` odata cu aplicatia.
   *
   * Sta pe GRUP, nu pe intrare: linkul apare doar acolo unde omul are oricum ce cauta. Grupurile
   * fara manual nu primesc link — mai bine lipsa decat o pagina care spune „inca nu s-a scris".
   */
  manual?: string
  /** Bani: grupul vazut doar de admin si contabilitate. Lacatul o spune in bara. */
  bani?: boolean
}

/*
  Bara laterala, regrupata pe zone de lucru (schelet aprobat de Madalin, 12.10.2026; macheta:
  https://claude.ai/artifact/Gegh5XAkRPK8oqofWCRvKy). Inainte grupurile urmau SURSA datelor —
  Shopify, Meta, eFactura, eMag, Amazon, fiecare grup al lui —, 13 grupuri cu 80 de intrari.
  Acum urmeaza munca: Acasa sus, apoi Vanzari, Magazine online, Marketing, Bani, Operatiuni,
  Firma & oameni, iar setarile intr-o singura pagina.

  Accesul NU s-a schimbat: fiecare intrare are aceeasi permisiune ca inainte, doar locul ei e
  altul. Paginile care se vor comasa (Clienti + Cohort, Performanta produse + Articole site,
  Campanii promo + flux, facturile eMag) raman deocamdata separate; se unesc in etapa B, modul
  cu modul.
*/

/** Paginile deschise de mai multe ori pe zi, mereu sus, fara titlu de grup. */
const ACASA: NavItem[] = [
  // Fara permisiune: e pagina de start a fiecaruia, iar login-ul trimite oricum aici (#786).
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/alerte', label: 'Centru alerte', icon: Bell, permission: 'alerts:read' },
  // O singura intrare pentru tot modulul: tabloul departamentului (12.10.2026). Activitatea,
  // arhiva si recurentele sunt vederi INAUNTRU.
  { to: '/task-uri', label: 'Task-uri', icon: CheckSquare, permission: 'tasks:read' },
  /*
    Mailul intreg, in CRM (Roundcube, 22.10.2026). „Istoric e-mailuri" a iesit odata cu el, iar
    „Mailul meu" (legatura casutei) s-a mutat in meniul contului si in pagina Mail, care il arata
    singura cand casuta lipseste.
  */
  { to: '/mail', label: 'Mail', icon: Mail, permission: 'mail:read' },
  // Ramane pentru casutele comune (contact@, contabilitate@), pana le deschide si Mail.
  { to: '/inbox', label: 'Inbox', icon: InboxIcon, permission: 'inbox:read' },
]

const NAV: NavGroup[] = [
  {
    label: 'Vânzări',
    icon: Briefcase,
    manual: '/manuale/index.html#vanzari',
    items: [
      { to: '/operational/comenzi', label: 'Comenzi', icon: ListChecks, permission: 'orders:read' },
      {
        to: '/clienti',
        label: 'Clienții mei',
        icon: Users,
        permission: 'orders:read',
      },
      {
        to: '/operational/sell-out',
        label: 'Sell-out clienți',
        icon: TrendingUp,
        permission: 'sellout:read',
      },
      {
        to: '/vanzari/discounturi',
        label: 'Discounturi acordate',
        icon: BadgePercent,
        permission: 'discounturi:read',
      },
      {
        to: '/preturi',
        label: 'Matrice prețuri & marje',
        icon: DollarSign,
        permission: 'prices.b2c:read',
      },
      {
        to: '/operational/cockpit-vanzari',
        label: 'Tablou de bord vânzări',
        section: 'Echipă',
        icon: ListChecks,
        permission: 'operational.agenti:read',
      },
      {
        to: '/operational/agenti-preview',
        label: 'Portofolii agenți (EA)',
        section: 'Echipă',
        icon: ListChecks,
        permission: 'operational.agenti:read',
      },
      {
        to: '/operational/alocare-clienti',
        label: 'Alocare clienți',
        section: 'Echipă',
        icon: Users,
        /*
          Permisiune PROPRIE, nu cea a tabloului de vanzari. Stateau sub aceeasi bifa, iar rolul
          `vanzari` o primise pentru tablou — deci fiecare agent vedea si ecranul de mutat
          clienti intre agenti (Victor Ciortan, 28.09.2026).
        */
        permission: 'operational.alocare:read',
      },
      {
        to: '/operational/corespondenta',
        label: 'Corespondență (AI)',
        section: 'Echipă',
        icon: Sparkles,
        permission: 'corespondenta:read',
      },
    ],
  },
  {
    label: 'Magazine online',
    icon: Store,
    manual: '/manuale/index.html#magazine-online',
    /*
      Facturile si incasarile eMag si Amazon stau AICI, la magazinul lor, nu in „Bani": le
      foloseste si marketingul, care le vede prin `emag:read` (Madalin, 12.10.2026: „nu vreau sa
      modificam accesul la rolul de Marketing momentan, membrii acestui rol au nevoie de datele
      respective"). In „Bani" stau doar paginile vazute numai de admin si contabilitate.
    */
    items: [
      {
        to: '/marketing/shopify/comenzi',
        label: 'Comenzi',
        section: 'Shopify',
        icon: Receipt,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/clienti',
        label: 'Clienți',
        section: 'Shopify',
        icon: Users,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/vouchere',
        label: 'Vouchere',
        section: 'Shopify',
        icon: Ticket,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/atributie',
        label: 'Atribuție UTM',
        section: 'Shopify',
        icon: TrendingUp,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/produse',
        label: 'Performanță produse',
        section: 'Shopify',
        icon: Package2,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/articole-site',
        label: 'Articole site',
        section: 'Shopify',
        icon: Barcode,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/marketing/shopify/cohort',
        label: 'Cohort retention',
        section: 'Shopify',
        icon: Grid3x3,
        permission: 'marketing.shopify:read',
      },
      {
        to: '/emag/dashboard',
        label: 'Dashboard',
        section: 'eMAG',
        icon: LayoutDashboard,
        permission: 'emag:read',
      },
      {
        to: '/emag/comenzi',
        label: 'Comenzi',
        section: 'eMAG',
        icon: Receipt,
        permission: 'emag:read',
      },
      {
        to: '/emag/facturi-vanzare',
        label: 'Facturi vânzare',
        section: 'eMAG',
        icon: FileText,
        permission: 'emag:read',
      },
      {
        to: '/emag/facturi-cumparare',
        label: 'Facturi cumpărare',
        section: 'eMAG',
        icon: FileText,
        permission: 'emag:read',
      },
      {
        to: '/emag/incasari',
        label: 'Încasări & costuri',
        section: 'eMAG',
        icon: Wallet,
        permission: 'emag:read',
      },
      {
        to: '/emag/vouchere',
        label: 'Vouchere',
        section: 'eMAG',
        icon: Ticket,
        permission: 'emag:read',
      },
      {
        to: '/amazon/facturi-vanzare',
        label: 'Facturi vânzare',
        section: 'Amazon',
        icon: Receipt,
        // TODO: permisiune dedicata 'amazon:read' (interim: refolosim emag:read)
        permission: 'emag:read',
      },
      {
        to: '/amazon/facturi-comisioane',
        label: 'Facturi servicii',
        section: 'Amazon',
        icon: Wallet,
        permission: 'emag:read',
      },
      {
        to: '/amazon/incasari',
        label: 'Încasări',
        section: 'Amazon',
        icon: Landmark,
        permission: 'emag:read',
      },
    ],
  },
  {
    label: 'Marketing',
    icon: Megaphone,
    manual: '/manuale/index.html#marketing',
    /*
      Ordinea NU e alfabetica si nu e istorica: e fluxul de lucru, de la ce se planifica pana la
      ce se face pe teren. Antetul de sectiune se scrie ori de cate ori sectiunea se schimba fata
      de randul de dinainte, deci elementele aceleiasi sectiuni TREBUIE sa stea lipite.

      Arhivate din bara laterala (Madalin, 08.10.2026): Generator etichete (/marketing/etichete),
      Randari 3D (/marketing/randari) si Banner Helper (/marketing/banner-helper). Paginile,
      rutele, API-ul si datele raman; se ajunge la ele prin URL. Pagina veche /marketing/print
      (Comenzi tipografie) la fel, pentru istoric.
    */
    items: [
      {
        to: '/marketing/evenimente',
        label: 'PR & Evenimente',
        section: 'Planificare',
        icon: Calendar,
        permission: 'marketing.events:read',
      },
      {
        to: '/marketing/promo',
        label: 'Campanii promo',
        section: 'Planificare',
        icon: BadgePercent,
        // Ecranul consuma /marketing/campaigns-simple, care cere marketing.campaigns.
        // Statea pe marketing.promo: cine avea promo vedea linkul si lua 403, iar cine
        // avea campaigns nu-l vedea desi avea dreptul.
        permission: 'marketing.campaigns:read',
      },
      {
        to: '/marketing/camppromo',
        label: 'Campanii promo - flux',
        section: 'Planificare',
        icon: Workflow,
        permission: 'marketing.promo:read',
      },
      {
        to: '/marketing/listari',
        label: 'Listări canale',
        section: 'Planificare',
        icon: ListChecks,
        permission: 'marketing.listings:read',
      },
      {
        to: '/marketing/campanii-mk',
        label: 'Campanii online',
        section: 'Online',
        icon: Rocket,
        permission: 'marketing.campaigns_mk:read',
      },
      {
        to: '/marketing/meta-ads',
        label: 'Meta Ads',
        section: 'Online',
        icon: TrendingUp,
        permission: 'marketing.meta:read',
      },
      {
        to: '/marketing/sampling',
        label: 'Acțiuni',
        section: 'Teren',
        icon: QrCode,
        permission: 'marketing.sampling:read',
      },
      {
        /*
          Expunerile stau pe teren, langa Actiuni: se pun fizic la partener. Pana pe 12.10.2026
          aveau sectiunea lor („expunere"), cu Campaniile online.
        */
        to: '/marketing/expuneri',
        label: 'Expuneri',
        section: 'Teren',
        icon: Rocket,
        permission: 'marketing.sampling:read',
      },
      {
        /*
          Cererile de marfa stau langa Actiuni, nu sub Campanii & Evenimente: intrarea aceea cere
          `marketing.events:read`, pe care rolurile `vanzari` si `administrativ` nu-l au — adica
          tocmai aprobatorii n-ar fi ajuns la lista.
        */
        to: '/marketing/cereri-marfa',
        label: 'Cereri de marfă',
        section: 'Teren',
        icon: PackageCheck,
        permission: 'marketing.sampling:read',
      },
      {
        to: '/marketing/sampling-stand',
        label: 'Predare la stand',
        section: 'Teren',
        icon: QrCode,
        permission: 'marketing.sampling:stand',
      },
      {
        to: '/marketing/cereri-print',
        label: 'Cereri tipografie & design',
        section: 'Producție',
        icon: Printer,
        permission: 'marketing.print:read',
      },
      {
        to: '/marketing/materiale',
        label: 'Materiale & kituri',
        section: 'Producție',
        icon: FolderOpen,
        permission: 'marketing.materials:read',
      },
      {
        // Medici, clinici, farmacii, branduri + mentenanta lunara (task #2516).
        to: '/marketing/colaborari',
        label: 'Colaborări',
        section: 'Parteneri',
        icon: Handshake,
        permission: 'colaborari:read',
      },
      {
        to: '/marketing/influenceri',
        label: 'Influenceri',
        section: 'Parteneri',
        icon: Users,
        permission: 'marketing.influencers:read',
      },
      {
        to: '/marketing/furnizori',
        label: 'Furnizori',
        section: 'Parteneri',
        icon: Truck,
        permission: 'marketing.suppliers:read',
      },
      {
        to: '/setari/agentii-marketing',
        label: 'Agenții marketing',
        section: 'Parteneri',
        icon: Handshake,
        permission: 'marketing.campaigns_mk:write',
      },
    ],
  },
  {
    label: 'Bani',
    icon: Wallet,
    bani: true,
    // Doua manuale in grup (rambursuri, taxe) — linkul duce la sectiunea Bani din cuprins.
    manual: '/manuale/index.html#bani',
    items: [
      {
        to: '/compliance/incasari',
        label: 'Extras de cont',
        section: 'Încasări',
        icon: DollarSign,
        permission: 'compliance:read',
      },
      {
        to: '/compliance/rambursuri-curieri',
        label: 'Rambursuri curieri',
        section: 'Încasări',
        icon: Truck,
        permission: 'fan:read',
      },
      {
        to: '/compliance/facturi-restante',
        label: 'Facturi furnizori',
        section: 'Facturi & SPV',
        icon: Receipt,
        permission: 'compliance:read',
      },
      {
        to: '/compliance/spv-ea',
        label: 'Reconciliere SPV ↔ EA',
        section: 'Facturi & SPV',
        icon: BookCheck,
        permission: 'compliance:read',
      },
      {
        to: '/efactura',
        label: 'Mesaje SPV',
        section: 'Facturi & SPV',
        icon: FileSpreadsheet,
        permission: 'anaf:read',
      },
      {
        to: '/emag/verificare',
        label: 'Verificare facturi eMAG',
        section: 'Facturi & SPV',
        icon: ShieldCheck,
        permission: 'compliance:read',
      },
      {
        to: '/bani/taxe',
        label: 'Taxe & impozite',
        section: 'Taxe',
        icon: Landmark,
        permission: 'taxe:read',
      },
      {
        to: '/bani/verificare-cifre',
        label: 'Verificare cifre',
        section: 'Taxe',
        icon: ShieldCheck,
        permission: 'verificareCifre:read',
      },
    ],
  },
  {
    label: 'Operațiuni',
    icon: Package,
    manual: '/manuale/index.html#operatiuni',
    items: [
      {
        to: '/operational/inventar-diferente',
        label: 'Diferențe inventar',
        icon: Boxes,
        permission: 'operational.inventar:read',
      },
      {
        to: '/operational/intrari',
        label: 'Intrări în gestiune',
        icon: PackageCheck,
        permission: 'operational.intrari:read',
      },
      {
        to: '/operational/expedieri-awb',
        label: 'Expedieri AWB',
        icon: Truck,
        permission: 'operational.expedieri:read',
      },
      {
        to: '/operational/rompetrol',
        label: 'Flotă Rompetrol',
        icon: Fuel,
        permission: 'operational.rompetrol:read',
        roluri: ROLURI_FLOTA,
      },
      {
        to: '/operational/flota/procese-verbale',
        label: 'Procese-verbale flotă',
        icon: ClipboardSignature,
        permission: 'operational.flota_pv:read',
        roluri: ROLURI_FLOTA,
      },
    ],
  },
  {
    label: 'Firmă & oameni',
    icon: Building2,
    manual: '/manuale/index.html#firma-oameni',
    items: [
      {
        to: '/intalniri',
        label: 'Întâlniri',
        section: 'Organizare',
        icon: Calendar,
        permission: 'meetings:read',
      },
      {
        to: '/concedii',
        label: 'Concedii',
        section: 'Organizare',
        icon: CalendarRange,
        permission: 'hr.concedii:read',
      },
      {
        to: '/contracte',
        label: 'Contracte',
        section: 'Organizare',
        icon: FileText,
        permission: 'contracts:read',
      },
      {
        to: '/onrc',
        label: 'ONRC',
        section: 'Organizare',
        icon: Building,
        permission: 'onrc:read',
      },
      {
        to: '/legal',
        label: 'Cazuri juridice',
        section: 'Organizare',
        icon: Scale,
        permission: 'legal:read',
      },
      {
        to: '/lab',
        label: 'Analize laborator',
        section: 'Organizare',
        icon: FlaskConical,
        permission: 'lab:read',
      },
      {
        to: '/hr/deconturi',
        label: 'Deconturi',
        section: 'Resurse umane',
        icon: Receipt,
        permission: 'hr.deconturi:read',
        roluri: ROLURI_DECONTURI_ECHIPA,
      },
      {
        to: '/hr/ordine-deplasare',
        label: 'Ordine deplasare',
        section: 'Resurse umane',
        icon: Calendar,
        permission: 'hr.ordine_deplasare:read',
      },
      { to: '/hr/incarca-bon', label: 'Decontul meu', section: 'Resurse umane', icon: Camera },
      // Fara permisiune, ca „Decontul meu": serverul ii arata omului doar masinile lui.
      { to: '/flota/masina-mea', label: 'Mașina mea', section: 'Resurse umane', icon: Car },
    ],
  },
]

/** O intrare cautabila: de unde vine ii spune omului unde o gaseste si fara cautare. */
interface Gasit {
  to: string
  label: string
  icon: LucideIcon
  unde: string
}

// Preferinte de afisare, pe browser: o pagina fixata sau bara ingusta nu sunt date de firma.
const citeste = <T,>(cheie: string, implicit: T): T => {
  try {
    const v = localStorage.getItem(cheie)
    return v === null ? implicit : (JSON.parse(v) as T)
  } catch {
    return implicit
  }
}
const scrie = (cheie: string, v: unknown) => {
  try {
    localStorage.setItem(cheie, JSON.stringify(v))
  } catch {
    // stocare blocata (fereastra privata) — preferinta nu se pastreaza, bara merge oricum
  }
}

const normalizeaza = (s: string) => s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()

interface SidebarProps {
  open: boolean
  onClose: () => void
}

/**
 * Grupurile si paginile pe care le vede omul, in ordinea din meniu. Aceeasi regula si pentru
 * „Acces rapid" de pe Dashboard — ce apare acolo trebuie sa fie exact ce gasesti in stanga.
 */
export function navigareVizibila(permissions: readonly string[], role?: string): NavGroup[] {
  const has = (p?: Permission) => !p || permissions.includes(p)
  const rolOk = (r?: readonly string[]) => !r || (!!role && r.includes(role))
  return NAV.map((g) => ({
    ...g,
    items: g.items.filter((it) => has(it.permission) && rolOk(it.roluri)),
  })).filter((g) => g.items.length > 0)
}

export function Sidebar({ open, onClose }: SidebarProps) {
  const permissions = useAuthStore((s) => s.permissions)
  const userId = useAuthStore((s) => s.user?.id)
  const location = useLocation()
  const navigate = useNavigate()
  const has = (p?: Permission) => !p || permissions.includes(p)

  // Insigna de langa Task-uri: cate asteapta sa le preia cineva din departamentul tau (adminul:
  // din toate). Galbena, nu rosie — e munca de luat, nu o greseala. Cheia sta sub ['taskuri'],
  // ca orice mutare de pe tablou s-o improspateze.
  const dePreluatQuery = useQuery<{ dePreluat: number }>({
    queryKey: ['taskuri', 'de-preluat'],
    queryFn: async () => (await api.get('/tasks/de-preluat')).data,
    refetchInterval: 60_000,
    enabled: permissions.includes('tasks:read'),
  })
  const dePreluat = dePreluatQuery.data?.dePreluat ?? 0

  const acasa = useMemo(
    () => ACASA.filter((it) => has(it.permission)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [permissions],
  )
  const role = useAuthStore((s) => s.user?.role)
  const visibleGroups = useMemo(() => navigareVizibila(permissions, role), [permissions, role])
  const setari = useMemo(() => setariVizibile(permissions), [permissions])

  // Toate paginile la care omul are acces, pentru cautare si pentru „Fixate".
  const toate = useMemo<Gasit[]>(
    () => [
      ...acasa.map((it) => ({ ...it, unde: 'Acasă' })),
      ...visibleGroups.flatMap((g) =>
        g.items.map((it) => ({
          ...it,
          unde: it.section ? `${g.label} › ${it.section}` : g.label,
        })),
      ),
      ...setari.flatMap((s) => s.linkuri.map((l) => ({ ...l, unde: `Setări › ${s.titlu}` }))),
      ...(has('changelog:read')
        ? [{ to: '/ce-s-a-schimbat', label: 'Ce s-a schimbat', icon: Sparkles, unde: 'Jos' }]
        : []),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acasa, visibleGroups, setari, permissions],
  )

  const grupActiv = (g: NavGroup) => g.items.some((it) => location.pathname.startsWith(it.to))

  // Un singur grup deschis: cel al paginii curente, sau cel ales. Mai multe grupuri deschise
  // deodata impingeau restul barei sub marginea ecranului.
  const [deschis, setDeschis] = useState<string | null>(
    () => visibleGroups.find(grupActiv)?.label ?? null,
  )
  useEffect(() => {
    const g = visibleGroups.find(grupActiv)
    if (g) setDeschis(g.label)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, visibleGroups])

  const cheieFixate = `b4l.fixate.${userId ?? 'anonim'}`
  const [fixate, setFixate] = useState<string[]>(() => citeste(cheieFixate, []))
  useEffect(() => setFixate(citeste(cheieFixate, [])), [cheieFixate])
  const comutaFixat = (to: string) => {
    const nou = fixate.includes(to) ? fixate.filter((x) => x !== to) : [...fixate, to]
    setFixate(nou)
    scrie(cheieFixate, nou)
  }
  // ce nu mai e accesibil (rol schimbat, pagina scoasa) nu mai apare
  const paginiFixate = fixate
    .map((to) => toate.find((t) => t.to === to))
    .filter((t): t is Gasit => !!t)

  const [ingustaAleasa, setIngusta] = useState<boolean>(() => citeste('b4l.bara.ingusta', false))
  // Pe telefon bara e un sertar care se deschide peste pagina — acolo are loc pentru etichete.
  const [ecranLat, setEcranLat] = useState(() => window.matchMedia('(min-width: 1024px)').matches)
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)')
    const f = () => setEcranLat(mq.matches)
    mq.addEventListener('change', f)
    return () => mq.removeEventListener('change', f)
  }, [])
  const ingusta = ingustaAleasa && ecranLat
  const comutaIngusta = (v: boolean) => {
    setIngusta(v)
    scrie('b4l.bara.ingusta', v)
  }

  const [cautare, setCautare] = useState('')
  const campCautare = useRef<HTMLInputElement>(null)
  const rezultate = useMemo(() => {
    const q = normalizeaza(cautare.trim())
    if (!q) return []
    return toate.filter((t) => normalizeaza(`${t.label} ${t.unde}`).includes(q))
  }, [cautare, toate])

  // ⌘K / Ctrl+K: cautarea din bara, de oriunde
  useEffect(() => {
    const k = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        comutaIngusta(false)
        requestAnimationFrame(() => campCautare.current?.focus())
      }
    }
    window.addEventListener('keydown', k)
    return () => window.removeEventListener('keydown', k)
  }, [])

  const mergi = (to: string) => {
    setCautare('')
    navigate(to)
    onClose()
  }

  const insignaTask = (to: string) =>
    to === '/task-uri' && dePreluat > 0 ? (
      <span
        className="rounded-full bg-b4l-amber px-1.5 py-0.5 text-[10px] font-bold leading-none text-b4l-pe-accent"
        title={`${dePreluat} de preluat`}
        aria-label={`${dePreluat} de preluat`}
      >
        {dePreluat}
      </span>
    ) : null

  const intrare = (it: { to: string; label: string; icon: LucideIcon }) => {
    const Icon = it.icon
    const fixat = fixate.includes(it.to)
    return (
      <div key={it.to} className="group/intrare relative">
        <NavLink
          to={it.to}
          onClick={onClose}
          title={ingusta ? it.label : undefined}
          className={({ isActive }) =>
            [
              'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition',
              ingusta ? 'justify-center' : 'pr-7',
              isActive
                ? 'bg-b4l-blue/25 font-medium text-white'
                : 'text-white/75 hover:bg-white/5 hover:text-white',
            ].join(' ')
          }
        >
          <Icon className="h-4 w-4 shrink-0" strokeWidth={2} />
          {!ingusta && <span className="flex-1 truncate">{it.label}</span>}
          {!ingusta && insignaTask(it.to)}
        </NavLink>
        {!ingusta && (
          <button
            type="button"
            onClick={() => comutaFixat(it.to)}
            aria-label={fixat ? `Scoate ${it.label} din fixate` : `Fixează ${it.label} sus`}
            title={fixat ? 'Scoate din fixate' : 'Fixează sus'}
            className={`absolute right-1 top-1/2 -translate-y-1/2 rounded p-1 transition ${
              fixat
                ? 'text-amber-400'
                : 'text-white/40 opacity-0 hover:text-white focus-visible:opacity-100 group-hover/intrare:opacity-100'
            }`}
          >
            <Star className="h-3.5 w-3.5" fill={fixat ? 'currentColor' : 'none'} />
          </button>
        )}
      </div>
    )
  }

  const titlu = (text: ReactNode) =>
    ingusta ? (
      <div className="mx-2 my-2 border-t border-white/10" />
    ) : (
      <div className="flex items-center gap-1.5 px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-white/40">
        {text}
      </div>
    )

  const setariActiva = location.pathname === '/setari'

  return (
    <>
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black/50 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}
      <aside
        className={[
          'fixed inset-y-0 left-0 z-40 flex flex-col border-r border-black/40 bg-[#1f1f24] text-white transition-[transform,width] duration-200 ease-out',
          ingusta ? 'w-16 min-w-16' : 'w-64 min-w-64',
          'lg:static lg:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        ].join(' ')}
      >
        <div className="flex items-center justify-end gap-1 px-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1.5 text-white/60 hover:bg-white/10 hover:text-white lg:hidden"
            aria-label="Închide meniul"
          >
            <X className="h-5 w-5" />
          </button>
          <button
            type="button"
            onClick={() => comutaIngusta(!ingustaAleasa)}
            className={`hidden rounded p-1.5 text-white/50 hover:bg-white/10 hover:text-white lg:block ${
              ingusta ? 'mx-auto' : ''
            }`}
            aria-label={ingusta ? 'Lărgește bara' : 'Strânge bara la iconițe'}
            title={ingusta ? 'Lărgește bara' : 'Strânge bara la iconițe'}
          >
            {ingusta ? (
              <PanelLeftOpen className="h-4 w-4" />
            ) : (
              <PanelLeftClose className="h-4 w-4" />
            )}
          </button>
        </div>

        {!ingusta && (
          <label className="mx-2 mb-1 mt-1 flex items-center gap-2 rounded-md border border-white/10 bg-white/5 px-2 py-1.5 text-white/50 focus-within:border-b4l-blue focus-within:text-white/80">
            <Search className="h-4 w-4 shrink-0" />
            <input
              ref={campCautare}
              value={cautare}
              onChange={(e) => setCautare(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') setCautare('')
                if (e.key === 'Enter' && rezultate[0]) mergi(rezultate[0].to)
              }}
              placeholder="Caută pagină"
              aria-label="Caută o pagină"
              className="min-w-0 flex-1 bg-transparent text-sm text-white placeholder:text-white/40 focus:outline-none"
            />
            <kbd className="rounded border border-white/15 px-1 font-mono text-[10px] text-white/40">
              ⌘K
            </kbd>
          </label>
        )}

        <nav className="flex-1 overflow-y-auto px-2 pb-3" aria-label="Meniu principal">
          {cautare.trim() ? (
            rezultate.length ? (
              <div className="mt-1 space-y-px">
                {rezultate.map((t) => (
                  <button
                    key={t.to}
                    type="button"
                    onClick={() => mergi(t.to)}
                    className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-white/80 hover:bg-white/5 hover:text-white"
                  >
                    <t.icon className="h-4 w-4 shrink-0" strokeWidth={2} />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">{t.label}</span>
                      <span className="block truncate text-[11px] text-white/45">{t.unde}</span>
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              <p className="px-2 py-6 text-center text-sm text-white/45">
                Nicio pagină cu „{cautare.trim()}”.
              </p>
            )
          ) : (
            <>
              {paginiFixate.length > 0 && (
                <>
                  {titlu(
                    <>
                      <Star className="h-3 w-3" /> Fixate
                    </>,
                  )}
                  <div className="space-y-px">{paginiFixate.map((t) => intrare(t))}</div>
                </>
              )}

              {acasa.length > 0 && (
                <>
                  {titlu('Acasă')}
                  <div className="space-y-px">{acasa.map((it) => intrare(it))}</div>
                </>
              )}

              {titlu('Module')}
              <div className="space-y-0.5">
                {visibleGroups.map((group) => {
                  const GroupIcon = group.icon
                  const activ = grupActiv(group)
                  const expanded = !ingusta && deschis === group.label
                  return (
                    <div key={group.label}>
                      <button
                        type="button"
                        onClick={() => {
                          if (ingusta) {
                            comutaIngusta(false)
                            setDeschis(group.label)
                          } else setDeschis(expanded ? null : group.label)
                        }}
                        aria-expanded={expanded}
                        title={ingusta ? group.label : undefined}
                        className={`flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-sm transition ${
                          ingusta ? 'justify-center' : ''
                        } ${
                          activ
                            ? 'font-medium text-white'
                            : 'text-white/80 hover:bg-white/5 hover:text-white'
                        }`}
                      >
                        <span
                          className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md ${
                            activ ? 'bg-sky-500/25' : 'bg-white/10'
                          }`}
                        >
                          <GroupIcon
                            className={`h-4 w-4 ${activ ? 'text-sky-300' : 'text-white/75'}`}
                            strokeWidth={2.2}
                          />
                        </span>
                        {!ingusta && (
                          <span className="flex-1 truncate text-left">{group.label}</span>
                        )}
                        {!ingusta && group.bani && (
                          <span
                            className="inline-flex items-center gap-0.5 rounded-full bg-amber-500/20 px-1.5 py-px text-[10px] font-semibold text-amber-300"
                            title="Doar Admin și Contabilitate"
                          >
                            <Lock className="h-2.5 w-2.5" /> restricționat
                          </span>
                        )}
                        {!ingusta && (
                          <ChevronRight
                            className={`h-4 w-4 text-white/45 transition-transform ${
                              expanded ? 'rotate-90' : ''
                            }`}
                          />
                        )}
                      </button>
                      {expanded && (
                        <div className="mb-1 ml-[18px] mt-0.5 space-y-px border-l border-white/10 pl-2">
                          {group.manual && (
                            // SUS, nu jos: pus la finalul listei cadea sub marginea ecranului
                            // (Alexandru, 24.09.2026). `<a>`, nu `NavLink`: manualul e un
                            // fisier din `public/`, nu o ruta a aplicatiei.
                            <a
                              href={group.manual}
                              target="_blank"
                              rel="noreferrer"
                              className="mb-1 flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-white/50 transition hover:bg-white/5 hover:text-white/80"
                            >
                              <BookOpen className="h-3.5 w-3.5 shrink-0" strokeWidth={2} />
                              <span className="flex-1 truncate">Instrucțiuni</span>
                            </a>
                          )}
                          {group.items.map((it, i) => (
                            <div key={it.to}>
                              {/* Titlul de sectiune apare la prima intrare VIZIBILA a ei. */}
                              {it.section != null && it.section !== group.items[i - 1]?.section && (
                                <div className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-white/40">
                                  {it.section}
                                </div>
                              )}
                              {intrare(it)}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </nav>

        <div className="space-y-px border-t border-white/10 px-2 py-2">
          {setari.length > 0 && (
            <NavLink
              to="/setari"
              onClick={onClose}
              title={ingusta ? 'Setări' : undefined}
              className={[
                'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm transition',
                ingusta ? 'justify-center' : '',
                setariActiva || location.pathname.startsWith('/setari/')
                  ? 'bg-b4l-blue/25 font-medium text-white'
                  : 'text-white/75 hover:bg-white/5 hover:text-white',
              ].join(' ')}
            >
              <Settings className="h-4 w-4 shrink-0" />
              {!ingusta && <span className="flex-1">Setări</span>}
            </NavLink>
          )}
          {has('changelog:read') &&
            // Doar admini (`changelog:read`): textele vin din mesajele de commit, unde scriu
            // despre bug-uri si presupuneri gresite. Util pentru cine conduce, derutant pentru rest.
            intrare({ to: '/ce-s-a-schimbat', label: 'Ce s-a schimbat', icon: Sparkles })}
          <a
            href="/manuale/index.html"
            target="_blank"
            rel="noreferrer"
            title={ingusta ? 'Instrucțiuni' : undefined}
            className={`flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-white/60 transition hover:bg-white/5 hover:text-white ${
              ingusta ? 'justify-center' : ''
            }`}
          >
            <BookOpen className="h-4 w-4 shrink-0" />
            {!ingusta && <span className="flex-1">Instrucțiuni</span>}
          </a>
        </div>
      </aside>
    </>
  )
}
